import { useRef, useEffect } from 'react';
import { Grid, Box, Alert, AlertTitle, Divider, Typography } from '@mui/material';
import PaymentSubmittionForm from './form/index';
import { useTokenDecode } from '../../hooks/data/useTokenDecode';
import { useSubmitPayment } from '../../hooks/data/useSubmitPayment';
import { useForm, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { TransactionStatus } from 'enums';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';
import { usePaymentMethod } from '..';
import { useTransactionPaymentCardValidation } from 'utils/validation/payments/transaction-payment-card-submittion';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import { useMe } from 'hooks/useMe';
import CardForms from './form/card-forms';

const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const PaymentMethodCard = ({ transactionDetailsQuery, token }) => {
    const { whoami } = useMe();
    const validationSchema = useTransactionPaymentCardValidation();
    const resolver = useYupValidationResolver(validationSchema);
    const methods = useForm({
        defaultValues: {
            email: 'test@test.gr',
            phoneCode: transactionDetailsQuery.data?.PhoneCode,
            billCountry: transactionDetailsQuery.data?.BillCountry,
            billCity: 'Athens',
            billAddress: transactionDetailsQuery.data?.BillAddress,
            billZip: transactionDetailsQuery.data?.BillZip
        },
        resolver,
        mode: 'onSubmit',
        reValidateMode: 'onBlur'
    });
    const { transactionId } = useTokenDecode();
    const { submitPayment } = useSubmitPayment();

    const cardEnabled = transactionDetailsQuery.data?.CardEnabled;

    const reCaptchaRef = useRef();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { selectedPaymentMethod } = usePaymentMethod();

    const transactionStatus = transactionDetailsQuery.data?.StatusId;
    const calculatedSum = transactionDetailsQuery.data?.Total ?? 0;

    const onSubmit = async (data) => {
        try {
            const captcha = await reCaptchaRef.current.executeAsync();
            console.log('data', data);
            await submitPayment({
                transactionId,
                paymentMethod: selectedPaymentMethod,
                captcha,
                token,
                paidAmount: calculatedSum,
                userId: whoami?.userId,
                ...data
            });
        } catch (error) {
            enqueueSnackbar(error?.message, {
                variant: 'error'
            });
        }
    };

    const readOnly = transactionStatus !== TransactionStatus.pendingPayment && transactionStatus !== TransactionStatus.rechecked;
    return (
        <>
            {cardEnabled ? (
                <>
                    <FormProvider {...methods}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h4" fontSize="16px">
                                    {t('SelectedPaymentMethod')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <PaymentSubmittionForm
                                    readOnly={readOnly}
                                    data={transactionDetailsQuery.data}
                                    reCaptchaRef={reCaptchaRef}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ReCAPTCHA sitekey={recaptchaKey} size="invisible" ref={reCaptchaRef} style={{ visibility: 'hidden' }} />
                            </Grid>
                            {(transactionStatus === TransactionStatus.pendingPayment ||
                                transactionStatus === TransactionStatus.rechecked) && (
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="end">
                                        <LoadingButton
                                            loading={methods?.formState.isSubmitting}
                                            disableElevation
                                            disabled={readOnly}
                                            size="large"
                                            variant="contained"
                                            sx={{ alignSelf: 'end' }}
                                            onClick={methods?.handleSubmit(onSubmit)}
                                        >
                                            {t('Payment')}
                                        </LoadingButton>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </FormProvider>
                    <CardForms />
                </>
            ) : null}
        </>
    );
};

PaymentMethodCard.propTypes = {
    // react query object
    transactionDetailsQuery: PropTypes.object,
    // token from the url
    token: PropTypes.string
};

export default PaymentMethodCard;
